/**
 * The step rail for the keyboard: which key does what, and what each step is
 * called out loud. The decisions, with no browser in them — `rowMenu.ts`'s
 * discipline — so a test can hold the keys and the words still.
 *
 * WHY A LISTBOX, and not a slider. The rail is a timeline you scrub, which reads
 * like a slider, but a slider's arrows CHANGE ITS VALUE: every press would seek
 * the replay, and the graph would re-draw a step at a time under a reader who
 * was only looking for one. The pointer never worked that way — sweeping the
 * rail raises a card and a click seeks — and the keyboard gets the same two
 * gestures: the arrows move a highlight, the card rises on it, and Enter seeks.
 * That is a single-select listbox with an active descendant: one tab stop, the
 * arrows walk the steps, each step is announced by name, and the step the
 * replay stands on is the selected one. A grid of buttons with a roving
 * tabindex would say the same with every step a separate control and nothing
 * saying which one is current.
 *
 * WHY SHIFT+B. The branch press on the card is pointer-aimed and hidden from the
 * accessibility tree (see the card in `Ledger.tsx`), so the focused step needs a
 * key of its own. A bare letter is the verbs' (D161), and a bare B is the Branch
 * VERB — a paid pass on the selected node — which must never be one slipped
 * finger away from a fork that switches the canvas. So the letter carries a
 * modifier. Shift and not ⌘ or Ctrl: the browser keeps several ⌘-letter chords
 * for itself (Firefox opens its bookmarks on ⌘B), and ⌘ here is the app's own
 * chord space. A focused listbox already keeps its letters from the verbs
 * (`letterKeyMayAct` names the role), so an unmodified B on the rail does
 * nothing, which is the honest answer to a key that means something else there.
 */

/** How far PageUp and PageDown move the highlight. */
export const RAIL_PAGE = 5

export type RailAct =
  /** Move the highlight to this step (0-based); nothing seeks. */
  | { kind: 'move'; to: number }
  /** Seek the replay to this step. */
  | { kind: 'seek'; at: number }
  /** Fork the exploration at this step (`forkAtStep`). */
  | { kind: 'branch'; at: number }

/**
 * What one key press on the focused rail does, from the step the highlight is on.
 * Null for a key the rail leaves alone — Space among them, which stays the
 * replay's play and pause everywhere, and every chord, which stays the app's.
 * The ends are walls, not a ring: a timeline has a first step and a last one,
 * and an arrow at the end that jumped to the other end would read as a seek.
 */
export function railKey(
  e: { key: string; shiftKey: boolean; metaKey?: boolean; ctrlKey?: boolean; altKey?: boolean },
  at: number,
  total: number,
): RailAct | null {
  if (total < 1 || e.metaKey || e.ctrlKey || e.altKey) return null
  const last = total - 1
  const to = (n: number): RailAct => ({ kind: 'move', to: Math.max(0, Math.min(last, n)) })
  switch (e.key) {
    // Along the footer, the steps run left to right (D369); the vertical
    // arrows keep the meaning they had when the rail stood on the field's edge,
    // so a hand that learned either finds the next step under it.
    case 'ArrowRight':
    case 'ArrowDown':
      return to(at + 1)
    case 'ArrowLeft':
    case 'ArrowUp':
      return to(at - 1)
    case 'PageDown':
      return to(at + RAIL_PAGE)
    case 'PageUp':
      return to(at - RAIL_PAGE)
    case 'Home':
      return to(0)
    case 'End':
      return to(last)
    case 'Enter':
      return { kind: 'seek', at: Math.max(0, Math.min(last, at)) }
  }
  if (e.shiftKey && e.key.toLowerCase() === 'b')
    return { kind: 'branch', at: Math.max(0, Math.min(last, at)) }
  return null
}

/**
 * A step's accessible name: its number, what landed it, what it says, the colour
 * it wears in words, and what it drew — "Step 3 of 12, Challenge: “Shielding is
 * forbidden outright”, contradiction, drew $0.30". Everything the card shows a
 * sighted reader, in the order the card shows it, because the card itself is
 * hidden from the accessibility tree. A pruned step has no claim left and says
 * that instead.
 */
export function stepName(o: {
  /** 0-based. */
  ix: number
  total: number
  /** What landed it — the rail's own `originWord`. */
  verb: string
  /** The node's label, or null when the step's node has been pruned. */
  label: string | null
  /** The channel's word (`CHANNEL_WORD`), lower-cased here mid-sentence. */
  channel: string
  /** The figure clause: "drew $0.30", "free", or "no figure". */
  drew: string
  /** The pruned word, from the vocabulary table. */
  pruned: string
}): string {
  const head = `Step ${o.ix + 1} of ${o.total}`
  if (o.label === null) return `${head}, ${o.pruned.toLowerCase()}`
  return `${head}, ${o.verb}: “${o.label}”, ${o.channel.toLowerCase()}, ${o.drew}`
}

/** What the rail says once Enter has sought — the replay's new position, or live. */
export function seekSaid(ix: number, total: number): string {
  return ix + 1 >= total
    ? `Back to live, at step ${total} of ${total}.`
    : `Replay at step ${ix + 1} of ${total}.`
}
